import React, { useCallback, useEffect, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import { CheckboxBlockField } from '@app/form';
import {
  Criterion,
  FieldChoice,
  FieldType,
  MultipleChoiceField,
  Quota,
  SingleChoiceField,
} from 'queries';

import { LogicJumpOption } from '../elements/interface';
import { useOnLogicJumpChange } from '../../hooks/useOnLogicJumpChange';

import { Autocomplete } from '../../Fields/Autocomplete';
import { ChoicesLabel } from '../../Fields/MultipleChoice/ChoicesLabel';
import { LogicAndValidationChoiceButtons } from '../elements/LogicAndValidationChoiceButtons';
import { useCriterionChoiceProps } from '../lib/useCriterionChoiceProps';
import { findJump } from '../lib/findJump';
import { FieldQuota } from 'components/CustomForm/FormBuilder/elements/FieldQuota';
import { ChoiceTagButton } from '../elements/ChoiceTagButton';

const useStyles = createUseStyles((theme: Theme) => ({
  checkboxPreview: {
    backgroundColor: theme.colors.white,
  },
  choiceContainer: {
    marginTop: theme.marginBase * 2,
    display: 'flex',
    flexDirection: 'row',
    gap: `${theme.marginBase}px`,
  },
}));

interface Props<F extends SingleChoiceField | MultipleChoiceField> {
  field: F;
  onChange: (field: F) => any;
  logicJumpOptions?: LogicJumpOption[];
  criterion?: Criterion;
  onCriterionChange?: (criterion: Criterion) => any;
  mustChooseValidation?: boolean;
  enabledTolerate?: boolean;
  numberOfParticipantRequired?: number;
  sessionId?: string;
  quotas?: Quota[] | null;
}

export const SelectChoicePreview = <
  F extends SingleChoiceField | MultipleChoiceField,
>({
  field,
  onChange,
  logicJumpOptions,
  criterion,
  onCriterionChange,
  mustChooseValidation,
  enabledTolerate,
  sessionId,
  quotas,
}: Props<F>) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const multipleChoices = field.type === FieldType.MULTIPLE_CHOICE;
  const showQuotas = !!quotas?.length;

  const choices = useMemo(() => field?.properties?.choices || [], [field]);
  const onLogicJumpOptionChange = useOnLogicJumpChange(field, onChange);

  const {
    isChoiceDeclined,
    isChoiceAccepted,
    isChoiceTolerated,
    acceptAllChoices,
    onAcceptClick,
    onDeclineClick,
    onTolerateClick,
  } = useCriterionChoiceProps({
    field,
    criterion,
    onCriterionChange,
    enabledTolerate,
  });

  useEffect(() => {
    if (!criterion && mustChooseValidation) {
      acceptAllChoices();
    }
  }, [
    acceptAllChoices,
    choices,
    criterion,
    mustChooseValidation,
    onAcceptClick,
  ]);

  const onChoiceChange = useCallback(
    (index: number, choice: FieldChoice) => {
      const newChoices = [...choices];
      newChoices[index] = choice;
      onChange({
        ...field,
        properties: {
          ...field.properties,
          choices: newChoices,
        },
      });
    },
    [onChange, field, choices],
  );

  if (field.properties.autocomplete) {
    return <Autocomplete field={field} isPreview />;
  }

  return (
    <>
      <ChoicesLabel field={field} />
      {choices.map((choice, index) => {
        const jump = field.jumpActions?.find((j) =>
          j.conditionChoiceRefs?.includes(choice.ref),
        );
        const selectedLogicJumpOption = findJump(logicJumpOptions, jump);
        return (
          <div
            className={classes.choiceContainer}
            key={`choice-${choice.ref}-${index}`}
          >
            <CheckboxBlockField
              className={classes.checkboxPreview}
              type={multipleChoices ? 'checkbox' : 'radio'}
              label={
                choice.label || t('screeningForm.noLabel', { index: index + 1 })
              }
              disabled
            />
            <LogicAndValidationChoiceButtons
              choice={choice}
              onLogicJumpOptionChange={onLogicJumpOptionChange}
              logicJumpOptions={logicJumpOptions}
              selectedLogicJumpOption={selectedLogicJumpOption}
              accepted={isChoiceAccepted(choice)}
              declined={isChoiceDeclined(choice)}
              tolerated={isChoiceTolerated(choice)}
              onTolerateClick={enabledTolerate ? onTolerateClick : undefined}
              onAcceptClick={onAcceptClick}
              onDeclineClick={onDeclineClick}
              mustChooseValidation={mustChooseValidation}
            />
            <ChoiceTagButton
              onChange={onChoiceChange}
              choice={choice}
              index={index}
              sessionId={sessionId}
            />
            {showQuotas && (
              <FieldQuota
                disabled
                key={`quota_${choice.ref}`}
                choice={choice.ref}
                quota={quotas?.find(
                  (c) => String(c.item) === String(choice.ref),
                )}
                quotas={quotas}
              />
            )}
          </div>
        );
      })}
    </>
  );
};
